import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/woocommerce";
import { toast, Toaster } from "sonner";
import {
  Sun, Moon, CheckCircle2, ChevronRight, Menu, X, Store, Smartphone,
  ScanLine, LayoutDashboard, Bell, CreditCard, TrendingUp, Globe,
  RefreshCw, Shield, Users, History, QrCode, Gift, Loader2, ArrowRight,
  FileSpreadsheet, PlugZap, BarChart3, Package, BookOpen, Mail, MessageCircle
} from "lucide-react";
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { QRCodeSVG } from 'qrcode.react';
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { AdZone } from "../components/AdZone";
import { logError } from "../lib/serializeError";

import logoFullLight from "../../assets/logo-light.png";
import logoFullDark from "../../assets/logo-dark.png";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');
const PLATFORM_DOMAIN = import.meta.env.VITE_PLATFORM_DOMAIN || 'lojapilot.pt';

// ── Hero dashboard mock (per newUI.html reference) ────────────────────────────
const DashboardMock = () => (
  <div className="relative rounded-3xl border border-white/15 bg-gradient-to-br from-white to-slate-100 p-3 shadow-[0_35px_90px_rgba(0,0,0,0.45)] lg:rotate-[-2deg]">
    <div className="flex items-center justify-between px-2 pb-2 text-[10px] text-slate-500">
      <div className="flex gap-1.5">
        <span className="w-2 h-2 rounded-full bg-slate-300" />
        <span className="w-2 h-2 rounded-full bg-slate-300" />
        <span className="w-2 h-2 rounded-full bg-slate-300" />
      </div>
      <span className="font-mono">lojapilot · dashboard</span>
      <span className="tracking-widest">···</span>
    </div>
    <div className="grid grid-cols-[92px_1fr] rounded-xl overflow-hidden bg-slate-50 min-h-[280px]">
      <div className="bg-slate-100 border-r border-slate-200 p-2.5 space-y-1.5">
        <div className="text-[10px] font-extrabold text-slate-900 mb-3 flex items-center gap-1">
          <img src="/brand/lojapilot-app-icon.png" alt="" className="w-3.5 h-3.5" /> LojaPilot
        </div>
        {[
          { label: 'Dashboard', icon: LayoutDashboard, active: true },
          { label: 'Produtos', icon: Package },
          { label: 'Encomendas', icon: Store },
          { label: 'Clientes', icon: Users },
          { label: 'Marketing', icon: Bell },
          { label: 'Definições', icon: RefreshCw },
        ].map((item) => (
          <div key={item.label} className={`text-[9px] px-1.5 py-1 rounded flex items-center gap-1 ${item.active ? 'bg-emerald-100 text-emerald-700 font-bold' : 'text-slate-500'}`}>
            <item.icon className="w-2.5 h-2.5" /> {item.label}
          </div>
        ))}
      </div>
      <div className="p-3.5">
        <p className="text-[11px] font-extrabold text-slate-900 mb-2.5">Visão geral da loja</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Vendas hoje', value: '€1.248', delta: '+12,4%' },
            { label: 'Encomendas', value: '48', delta: '+8,2%' },
            { label: 'Clientes', value: '320', delta: '+15%' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-2">
              <p className="text-[8px] text-slate-500">{s.label}</p>
              <p className="text-sm font-extrabold text-slate-900 my-0.5">{s.value}</p>
              <p className="text-[8px] text-emerald-600 font-semibold">{s.delta}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[1.1fr_.9fr] gap-2 mt-2">
          <div className="bg-white border border-slate-200 rounded-lg p-2.5">
            <p className="text-[9px] font-bold text-slate-700 mb-2">Vendas ao longo do tempo</p>
            <div className="h-[72px] flex items-end gap-1.5 border-b border-slate-200 px-1 bg-[repeating-linear-gradient(to_bottom,transparent_0_22px,#e2e8f0_23px)]">
              {[28, 42, 35, 58, 48, 73, 63, 91, 78].map((h, i) => (
                <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-emerald-600 to-emerald-400" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-2.5">
            <p className="text-[9px] font-bold text-slate-700 mb-2">Mais vendidos</p>
            {[['Produto A', '€24,90'], ['Produto B', '€18,50'], ['Produto C', '€12,00']].map(([n, p]) => (
              <div key={n} className="flex justify-between text-[9px] text-slate-600 py-1.5 border-b border-slate-100 last:border-0">
                <span>{n}</span><b className="text-emerald-600">{p}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <div className="absolute -right-3 bottom-7 bg-white text-slate-900 rounded-2xl px-3.5 py-3 shadow-xl flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-600 grid place-items-center">
        <TrendingUp className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[11px] font-bold">Loja em crescimento</p>
        <p className="text-[9px] text-slate-500">Vendas e stock num só lugar</p>
      </div>
    </div>
  </div>
);

const ScannerPhone = () => (
  <div className="relative mx-auto w-[240px] sm:w-[280px] h-[500px] sm:h-[560px] bg-black rounded-[42px] shadow-[0_0_0_8px_#1f2937,0_20px_40px_-10px_rgba(0,0,0,0.5)] overflow-hidden border-[6px] border-black">
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[90px] h-[26px] bg-black rounded-[18px] z-50" />
    <div className="w-full h-full bg-slate-950 flex items-center justify-center relative overflow-hidden p-4">
      <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-emerald-500/25 blur-2xl" />
      <div className="absolute -bottom-10 -left-10 w-28 h-28 rounded-full bg-emerald-500/15 blur-2xl" />
      {/* Cartão de fidelização LojaPilot — mesmo layout do WebPass do cliente */}
      <div className="relative w-full max-w-[210px] aspect-[1/1.55] rounded-2xl border border-white/10 bg-gradient-to-b from-[#111827] to-black p-5 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div>
            <img src={logoFullLight} alt="LojaPilot" className="h-5 object-contain mb-1.5" />
            <p className="text-[8px] uppercase tracking-[0.22em] text-emerald-400 font-bold">Premium Card</p>
          </div>
          <Gift className="w-5 h-5 text-emerald-400 opacity-90" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="p-2.5 bg-white rounded-xl shadow-xl">
            <QRCodeSVG
              value={JSON.stringify({ customerId: 'demo', s: 'lojapilot' })}
              size={108}
              level="H"
              includeMargin={false}
              fgColor="#000000"
              bgColor="#ffffff"
            />
          </div>
        </div>
        <div className="pt-3 border-t border-white/10 text-center">
          <p className="text-[8px] text-white/50 leading-snug">Apresente este código na caixa para acumular ou resgatar pontos.</p>
        </div>
      </div>
    </div>
  </div>
);

export function LandingPage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(localStorage.getItem('landing_theme') || 'dark');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [isAnnual, setIsAnnual] = useState(true);

  const [leadForm, setLeadForm] = useState({ name: "", phone: "", countryCode: "+351", planOfInterest: "Business", email: "" });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const [dbPlans, setDbPlans] = useState<any[]>([]);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [checkoutData, setCheckoutData] = useState({ name: "", email: "", url: "" });
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    document.title = 'LojaPilot';
    const fetchPlans = async () => {
      const { data } = await supabase.from('subscription_plans').select('*').eq('active', true).order('monthly_price', { ascending: true });
      if (data) setDbPlans(data);
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    localStorage.setItem('landing_theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Scroll reveal: sections/cards marked .lp-reveal fade+rise in when they
  // enter the viewport. One-shot — the class stays so scrolling back up
  // doesn't re-hide content.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('.lp-reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('lp-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('lp-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // SwiftApps AI agent — landing page only. The script is injected on
  // mount (JSX never executes <script>) and widget nodes appended to
  // <body> are dropped on unmount, so the agent never reaches
  // login/dashboard/storefront.
  // If the script is blocked (ad-blocker, CSP, rede), show a contact
  // fallback instead of a silent void — and log a diagnostic.
  const [chatBlocked, setChatBlocked] = useState(false);
  useEffect(() => {
    const SRC = 'https://agent.swiftapps.cloud/widget/chatbot.js';
    const WIDGET_SEL = [
      'iframe[src*="swiftapps"]',
      '[id*="swiftchat"]', '[class*="swiftchat"]',
      '[id*="chatbot"]', '[class*="chatbot"]',
      '[id*="chat-widget"]', '[class*="chat-widget"]',
    ].join(', ');
    const CLEANUP_SEL = WIDGET_SEL.split(', ').map(s => `body > ${s}`).join(', ');
    const inject = () => {
      document.querySelectorAll(`script[src="${SRC}"]`).forEach(n => n.remove());
      const s = document.createElement('script');
      s.src = SRC;
      s.async = true;
      s.dataset.siteId = '3f0937019e8a';
      s.dataset.apiUrl = 'https://agent.swiftapps.cloud';
      s.onerror = () => setChatBlocked(true);
      document.head.appendChild(s);
    };
    inject();
    // One retry covers network races; afterwards we declare the widget
    // blocked and surface the contact fallback.
    const t1 = setTimeout(() => {
      if (!document.querySelector(WIDGET_SEL)) inject();
    }, 5000);
    const t2 = setTimeout(() => {
      if (!document.querySelector(WIDGET_SEL)) {
        console.warn('[LojaPilot] Chat agent não inicializou — script bloqueado (ad-blocker/CSP) ou indisponível.');
        setChatBlocked(true);
      }
    }, 14000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      // If the widget mounted stray nodes straight on <body>, drop them so
      // the agent stays landing-only.
      document.querySelectorAll(CLEANUP_SEL).forEach(n => n.remove());
    };
  }, []);

  const scrollTo = (id: string) => {
    setShowMobileMenu(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.phone) return;
    setIsSubmittingLead(true);
    try {
      const { data, error } = await supabase.functions.invoke('make-server-75caf0c1/leads', {
        method: 'POST',
        body: {
          name: leadForm.name, phone: leadForm.phone, email: leadForm.email,
          country_code: leadForm.countryCode,
          notes: `Interesse no plano: ${leadForm.planOfInterest}`
        }
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Falha ao guardar o pedido');
      try {
        await supabase.functions.invoke('send-lead-email', {
          body: { leadData: { name: leadForm.name, phone: leadForm.phone, email: leadForm.email, country_code: leadForm.countryCode, plan: leadForm.planOfInterest } }
        });
      } catch (emailErr) { console.warn("Could not send email, but lead was saved:", emailErr); }
      toast.success("Obrigado! A nossa equipa entrará em contacto em breve.");
      setLeadForm({ name: "", email: "", phone: "", countryCode: "+351", planOfInterest: "Business" });
    } catch (err: any) {
      logError('[Landing] Lead submit', err);
      toast.error("Erro ao enviar o pedido. Tente novamente.");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const handleCheckoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkoutData.name || !checkoutData.email || !checkoutData.url) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    setIsRedirecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-checkout', {
        body: { email: checkoutData.email, plan_id: selectedPlanId, name: checkoutData.name, store_url: checkoutData.url, is_annual: isAnnual, quantity }
      });
      if (error) throw error;
      if (data?.clientSecret) setClientSecret(data.clientSecret);
      else throw new Error(data?.error || 'Não foi possível gerar a sessão de pagamento (Falta Client Secret)');
    } catch (err: any) {
      // FunctionsHttpError serializa para {} — ler o body real da edge function
      let detail = err?.message || '';
      if (err?.context?.json) {
        try { detail = (await err.context.json())?.error || detail; } catch { /* ignore */ }
      }
      console.error('[Checkout]', detail || err);
      toast.error('Erro ao iniciar pagamento: ' + (detail || 'Erro desconhecido'));
    } finally {
      setIsRedirecting(false);
    }
  };

  const navItems = [
    { id: 'como-funciona', label: 'Como funciona' },
    { id: 'funcionalidades', label: 'Funcionalidades' },
    { id: 'woo-pos', label: 'WooCommerce + POS' },
    { id: 'scanner', label: 'Scanner QR' },
    { id: 'precos', label: 'Preços' },
    { id: 'faq', label: 'FAQ' },
  ];



  const features = [
    { icon: Smartphone, t: 'Loja PWA', d: 'Uma experiência rápida, instalável no ecrã inicial e pensada para mobile.' },
    { icon: Bell, t: 'Notificações push', d: 'Mantém os clientes a par de novidades e atualizações relevantes.' },
    { icon: CreditCard, t: 'Pagamentos diretos', d: 'Liga os teus fornecedores de pagamento suportados e recebe na tua conta.' },
    { icon: Package, t: 'Produtos e stock', d: 'Gere variantes, preços, inventário e encomendas num só local.' },
    { icon: BarChart3, t: 'Estatísticas claras', d: 'Acompanha vendas e desempenho com informação fácil de interpretar.' },
    { icon: Globe, t: 'Domínio da loja', d: 'Partilha um endereço próprio da plataforma e reforça a tua marca.' },
    { icon: RefreshCw, t: 'Importação e sync', d: 'Começa com CSV ou conecta o WooCommerce existente.' },
    { icon: TrendingUp, t: 'Preparado para crescer', d: 'Escolhe um plano adequado à fase atual do teu negócio.' },
  ];

  const wooCards = [
    { icon: LayoutDashboard, t: 'Gestão de WooCommerce', d: 'Acompanha produtos, stock, encomendas e clientes das lojas ligadas. Mantém os acessos e as permissões organizados.' },
    { icon: Users, t: 'Acesso para clientes', d: 'Dá aos clientes acesso à sua área para consultarem o saldo de pontos e o histórico, sem expor o painel de gestão.' },
    { icon: QrCode, t: 'POS Scanner por QR Code', d: 'Usa a câmara do telemóvel como scanner no balcão para identificar clientes e atribuir pontos rapidamente.' },
    { icon: Gift, t: 'Pontos online e offline', d: 'Unifica a fidelização entre o balcão e o WooCommerce. O cliente resgata pontos ou descontos no checkout online.' },
    { icon: History, t: 'Histórico em tempo real', d: 'Consulta movimentos de pontos e atividade dos clientes num fluxo centralizado.' },
    { icon: Shield, t: 'Permissões e privacidade', d: 'Define quem pode gerir a loja e que dados são apresentados a cada perfil.' },
  ];

  const faqs = [
    { q: 'Preciso de ter um site para usar o LojaPilot?', a: 'Não. Podes construir o teu catálogo de produtos do zero e obter a tua própria página de loja na plataforma. Se já usas WooCommerce, podes ligá-la em minutos.' },
    { q: 'Posso importar os meus produtos?', a: 'Sim — importação por CSV com mapeamento de colunas para migrar o catálogo rapidamente, logo no onboarding.' },
    { q: 'Como funcionam os pagamentos?', a: 'A plataforma foi desenhada para integrar fornecedores como o Stripe. A disponibilidade real depende de cada integração estar configurada e ativa.' },
    { q: 'A loja funciona no telemóvel?', a: 'Sim, a experiência é responsiva e PWA-ready — instalável como app onde o dispositivo e o navegador o suportem.' },
  ];

  return (
    <div className={`min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white transition-colors duration-300 font-sans overflow-x-hidden ${theme}`}>
      <Toaster position="top-center" />

      {/* HEADER */}
      <header className="fixed top-0 w-full z-[100] bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b dark:border-slate-800">
        <div className="max-w-screen-xl mx-auto px-3 sm:px-4 h-20 sm:h-24 flex items-center justify-between">
          <img src={theme === 'dark' ? logoFullLight : logoFullDark} alt="LojaPilot" className="h-16 sm:h-24 py-2 object-contain" />

          <nav className="hidden lg:flex items-center gap-5">
            {navItems.map((item) => (
              <button key={item.id} onClick={() => scrollTo(item.id)} className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors">
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Alternar tema">
              {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
            </button>
            <button onClick={() => navigate('/login')} className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors px-2">
              Iniciar Sessão
            </button>
            <button onClick={() => scrollTo('precos')} className="bg-emerald-500 hover:bg-emerald-600 text-[#03291F] px-5 py-2.5 rounded-full font-bold text-sm transition-colors flex items-center gap-1">
              Ver planos <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
            </button>
            <button className="p-2 relative z-[110]" onClick={() => setShowMobileMenu(!showMobileMenu)}>
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MENU */}
      <div className={`fixed inset-0 bg-white dark:bg-slate-950 z-[90] transition-transform duration-300 ease-in-out md:hidden flex flex-col pt-24 px-6 ${showMobileMenu ? 'translate-x-0' : 'translate-x-full'}`}>
        <nav className="flex flex-col gap-5 text-xl font-bold mt-8">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => scrollTo(item.id)} className="text-left py-3 border-b dark:border-slate-800 flex justify-between items-center">
              {item.label} <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          ))}
        </nav>
        <div className="mt-auto pb-12 flex flex-col gap-4">
          <button onClick={() => navigate('/login')} className="w-full py-4 rounded-xl border-2 border-slate-200 dark:border-slate-800 font-bold">Iniciar Sessão</button>
          <button onClick={() => navigate('/signup')} className="w-full bg-emerald-500 text-[#03291F] py-4 rounded-xl font-bold">Criar Loja Grátis</button>
        </div>
      </div>

      {/* HERO */}
      <section id="inicio" className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 bg-[radial-gradient(ellipse_at_78%_20%,#0c4560_0,transparent_35%),radial-gradient(ellipse_at_10%_100%,#0b3a36_0,transparent_35%),linear-gradient(125deg,#071426,#0b1b30_60%,#071426)] text-slate-50">
        <div className="absolute inset-0 opacity-[0.12] pointer-events-none bg-[linear-gradient(#7dd3fc15_1px,transparent_1px),linear-gradient(90deg,#7dd3fc15_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
        <div className="max-w-screen-xl mx-auto grid lg:grid-cols-[0.93fr_1.07fr] gap-12 items-center relative z-10 lp-reveal">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 border border-emerald-400/30 bg-emerald-500/10 text-emerald-300 rounded-full px-3.5 py-2 text-[11px] font-bold tracking-wide">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              O novo padrão para criar e gerir lojas online
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-extrabold tracking-[-0.04em] leading-[1.08] mt-6 mb-5 max-w-[620px] mx-auto lg:mx-0 lp-grad-light">
              Cria ou gere a tua loja online. <span className="lp-grad-green">WooCommerce, POS e fidelização.</span>
            </h1>
            <p className="text-slate-300/90 text-[15px] leading-relaxed max-w-[560px] mx-auto lg:mx-0 mb-7">
              Cria uma loja nova ou liga o teu WooCommerce existente para gerir produtos, stock e encomendas. Dá acesso aos clientes, utiliza o POS Scanner por QR Code e liga os pontos de fidelização entre o balcão e o checkout online.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <button onClick={() => navigate('/signup')} className="bg-emerald-500 hover:bg-emerald-400 text-[#03291F] px-7 py-3.5 rounded-full font-bold text-sm transition-colors inline-flex items-center justify-center gap-2">
                Criar Loja Grátis <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => scrollTo('como-funciona')} className="px-7 py-3.5 rounded-full font-bold text-sm border border-white/25 hover:border-emerald-400/60 text-slate-100 transition-colors inline-flex items-center justify-center gap-2">
                Ver como funciona
              </button>
            </div>
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start mt-6 text-[11px] text-slate-300/80">
              {['Sem cartão de crédito', 'Configuração rápida', 'Pagamentos seguros'].map((m) => (
                <span key={m} className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> {m}</span>
              ))}
            </div>
          </div>
          <DashboardMock />
        </div>
      </section>

      {/* COMO FUNCIONA — 3 routes. O ad vive dentro desta secção para
          herdar o fundo e ficar com espaçamento uniforme acima/abaixo. */}
      <section id="como-funciona" className="py-20 sm:py-24 px-4 bg-white dark:bg-slate-950">
        <div className="max-w-screen-xl mx-auto">
          <div className="mb-14 sm:mb-16 lp-reveal">
            <AdZone slot="landing_top" requireFreePlan={false} />
          </div>
          <div className="text-center max-w-2xl mx-auto mb-12 lp-reveal">
            <p className="lp-grad-green uppercase text-[10px] tracking-[0.16em] font-extrabold">Começa à tua maneira</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] mt-3 mb-3 lp-grad-dark">De onde queres arrancar? O LojaPilot adapta-se ao teu negócio.</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Não importa se estás a começar do zero ou se já tens uma operação em movimento. Nós integramos.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 lp-reveal">
            {[
              { icon: Store, t: 'Construtor Nativo', d: 'Se ainda não tens site, monta o teu catálogo de produtos do zero no nosso painel. Adiciona preços, variações e gere o stock num único sítio.', tag: '01 / Criar do zero' },
              { icon: FileSpreadsheet, t: 'Importação por CSV', d: 'Tens os teus produtos noutra plataforma ou numa folha de cálculo? Faz upload do teu ficheiro CSV, mapeia as colunas e migra o teu catálogo instantaneamente.', tag: '02 / Importar catálogo' },
              { icon: PlugZap, t: 'Conexão WooCommerce', d: 'Já usas o WooCommerce? Mantém a tua estrutura atual e liga-a ao LojaPilot para aproveitar uma vitrine otimizada, PWA rápida e pagamentos diretos.', tag: '03 / Sincronizar loja' },
            ].map((c) => (
              <button key={c.t} onClick={() => navigate('/signup')} className="text-left border border-slate-200 dark:border-slate-800 rounded-3xl bg-white dark:bg-slate-900 p-7 shadow-sm hover:-translate-y-1 hover:border-emerald-500/50 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 grid place-items-center mb-5"><c.icon className="w-6 h-6" /></div>
                <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white mb-2">{c.t}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-[13px] leading-relaxed mb-4">{c.d}</p>
                <span className="text-emerald-600 dark:text-emerald-400 text-[11px] font-extrabold inline-flex items-center gap-1">{c.tag} <ArrowRight className="w-3 h-3" /></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* URL / BRAND PANEL */}
      <section className="py-16 px-4 bg-white dark:bg-slate-950">
        <div className="max-w-screen-xl mx-auto rounded-[28px] bg-[linear-gradient(120deg,#0f172a,#10384a)] text-slate-50 p-8 sm:p-12 grid lg:grid-cols-[1fr_.9fr] gap-8 items-center overflow-hidden relative lp-reveal">
          <div>
            <p className="lp-grad-green uppercase text-[10px] tracking-[0.16em] font-extrabold">A tua marca, sem limites</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] leading-tight mt-3 mb-4 lp-grad-light">A tua marca, o teu endereço exclusivo.</h2>
            <p className="text-slate-300/90 text-sm max-w-lg leading-relaxed mb-6">Cada lojista ganha automaticamente a sua própria página otimizada na plataforma para partilhar com os clientes. Simples de divulgar, fácil de reconhecer.</p>
            <button onClick={() => navigate('/signup')} className="bg-emerald-500 hover:bg-emerald-400 text-[#03291F] px-6 py-3 rounded-full font-bold text-sm transition-colors inline-flex items-center gap-2">
              Criar o meu endereço <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-white text-slate-900 rounded-2xl p-5 shadow-2xl rotate-2 max-w-md w-full mx-auto">
            <div className="flex gap-1.5 border-b border-slate-200 pb-3 mb-4">
              <span className="w-2 h-2 rounded-full bg-slate-300" /><span className="w-2 h-2 rounded-full bg-slate-300" /><span className="w-2 h-2 rounded-full bg-slate-300" />
            </div>
            <div className="bg-slate-100 rounded-lg px-4 py-3 text-[13px] text-slate-600 font-mono flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span><b className="text-emerald-600">{PLATFORM_DOMAIN}</b> / minha-loja</span>
            </div>
            <div className="flex items-center justify-between gap-3 pt-4 px-1">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600 grid place-items-center"><Store className="w-4 h-4" /></div>
                <div>
                  <p className="text-sm font-bold">Minha Loja</p>
                  <p className="text-[10px] text-slate-500">A tua montra online</p>
                </div>
              </div>
              <span className="text-[11px] font-bold text-emerald-600 inline-flex items-center gap-1">Visitar loja <ArrowRight className="w-3 h-3" /></span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section id="funcionalidades" className="py-20 sm:py-24 px-4 bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12 lp-reveal">
            <p className="lp-grad-green uppercase text-[10px] tracking-[0.16em] font-extrabold">Tudo ligado</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] mt-3 mb-3 lp-grad-dark">Tudo o que precisas. Num único painel.</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Menos ferramentas dispersas, mais controlo sobre o teu negócio.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lp-reveal">
            {features.map((f) => (
              <div key={f.t} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:border-emerald-500/40 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 grid place-items-center mb-4"><f.icon className="w-5 h-5" /></div>
                <h3 className="font-bold text-sm text-slate-900 dark:text-white mb-1.5">{f.t}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WOO + POS */}
      <section id="woo-pos" className="py-20 sm:py-24 px-4 bg-white dark:bg-slate-950">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12 lp-reveal">
            <p className="lp-grad-green uppercase text-[10px] tracking-[0.16em] font-extrabold">WooCommerce, loja física e clientes</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] mt-3 mb-3 lp-grad-dark">A tua loja online e a experiência na loja física, ligadas.</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">O LojaPilot ajuda-te a gerir operações WooCommerce num só lugar, com acessos adequados para a equipa e uma experiência simples para os teus clientes.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lp-reveal">
            {wooCards.map((c) => (
              <div key={c.t} className="border border-slate-200 dark:border-slate-800 rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-sm">
                <div className="w-11 h-11 rounded-2xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 grid place-items-center mb-4"><c.icon className="w-5 h-5" /></div>
                <h3 className="font-heading font-bold text-slate-900 dark:text-white mb-2">{c.t}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-[13px] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-4 border border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50 dark:bg-slate-900 p-5">
            <div className="w-12 h-12 rounded-xl bg-[#7F54B3] text-white grid place-items-center font-black text-xl shrink-0">W</div>
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-sm">Ligação WooCommerce por API ou plugin</h3>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">Liga uma loja existente através de uma integração autenticada por API REST ou pelo plugin LojaPilot Connect, conforme a configuração suportada.</p>
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="font-bold text-sm text-slate-900 dark:text-white">Uma experiência unificada para comerciantes e clientes.</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">A integração e as funcionalidades disponíveis dependem da configuração da loja e dos módulos ativados.</p>
          </div>
        </div>
      </section>

      {/* SCANNER */}
      <section id="scanner" className="py-20 sm:py-24 px-4 bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-screen-xl mx-auto grid lg:grid-cols-2 gap-12 items-center lp-reveal">
          <div>
            <p className="lp-grad-green uppercase text-[10px] tracking-[0.16em] font-extrabold">Fidelização no balcão e online</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] mt-3 mb-4 lp-grad-dark">O cartão de cliente está no telemóvel. O scanner está pronto no balcão.</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8">O cliente abre a sua área no telemóvel e apresenta o QR Code pessoal. A equipa lê o código com a câmara do dispositivo POS para identificar o cliente e registar pontos.</p>
            <div className="space-y-5">
              {[
                { icon: QrCode, t: 'QR Code pessoal do cliente', d: 'Identificação rápida, sem cartão físico nem leitor dedicado.' },
                { icon: Gift, t: 'Pontos e saldo sincronizados', d: 'Consulta do saldo e movimentos; resgate online conforme as regras configuradas.' },
                { icon: ScanLine, t: 'Um fluxo para a equipa', d: 'Atribui pontos no balcão e mantém a experiência ligada à loja online.' },
              ].map((p) => (
                <div key={p.t} className="flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 grid place-items-center shrink-0"><p.icon className="w-5 h-5" /></div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">{p.t}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <ScannerPhone />
        </div>
      </section>

      {/* PRICING */}
      <section id="precos" className="py-20 sm:py-24 px-4 bg-white dark:bg-slate-950">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10 lp-reveal">
            <p className="lp-grad-green uppercase text-[10px] tracking-[0.16em] font-extrabold">Preços simples</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] mt-3 mb-3 lp-grad-dark">Começa pequeno. Cresce ao teu ritmo.</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Escolhe o plano certo para a fase do teu negócio. Sem surpresas.</p>
          </div>

          <div className="flex justify-center items-center gap-3 mb-10 text-sm">
            <span className={`font-semibold ${isAnnual ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>Anual</span>
            <button onClick={() => setIsAnnual(!isAnnual)} className="relative inline-flex h-7 w-14 items-center rounded-full bg-emerald-500 shadow" role="switch" aria-checked={isAnnual}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${isAnnual ? 'translate-x-8' : 'translate-x-1'}`} />
            </button>
            <span className={`font-semibold ${!isAnnual ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>Equivalente mensal</span>
          </div>

          {/* Planos sincronizados com o painel Master (subscription_plans):
              preço 0 → onboarding grátis; com Stripe price_id → checkout
              automático; pago sem price_id → contacto comercial. */}
          <div className={`grid gap-5 max-w-5xl mx-auto lp-reveal ${dbPlans.length > 3 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
            {dbPlans.map((plan) => {
              const isFree = !plan.monthly_price || plan.monthly_price === 0;
              const priceId = isAnnual ? (plan.stripe_annual_price_id || plan.stripe_monthly_price_id) : plan.stripe_monthly_price_id;
              const canCheckout = !isFree && !!priceId;
              const isPopular = plan.name?.toLowerCase() === 'premium';
              const price = isAnnual ? (plan.annual_price || plan.monthly_price) : plan.monthly_price;
              return (
                <div key={plan.id} className={`relative rounded-3xl bg-white dark:bg-slate-900 p-7 flex flex-col ${isPopular ? 'border-2 border-emerald-500 shadow-xl shadow-emerald-500/10' : 'border border-slate-200 dark:border-slate-800'}`}>
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-[#03291F] text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">Mais escolhido</div>
                  )}
                  <p className="font-heading font-extrabold text-lg text-slate-900 dark:text-white">{plan.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-5 min-h-[2rem]">{plan.description}</p>
                  <p className="text-4xl font-black text-slate-900 dark:text-white">
                    €{price}
                    <span className="text-sm font-semibold text-slate-400">{isAnnual && !isFree ? ' / ano' : ' / mês'}</span>
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 mb-1">
                    {isFree ? 'Plano gratuito · sem subscrição' : isAnnual ? 'Compromisso e pagamento anual' : 'Faturação mensal'}
                  </p>
                  {isFree && <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-4">Inclui espaços de publicidade LojaPilot.</p>}
                  {!isFree && <div className="mb-4" />}
                  <button
                    onClick={() => isFree ? navigate('/signup') : canCheckout ? (setSelectedPlanId(plan.id), setShowCheckoutModal(true)) : scrollTo('contact')}
                    className={`w-full py-3 rounded-xl font-bold text-sm transition-colors mb-6 ${isPopular ? 'bg-emerald-500 hover:bg-emerald-400 text-[#03291F]' : 'border-2 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                  >
                    {isFree ? 'Começar grátis' : canCheckout ? `Escolher ${plan.name}` : 'Falar connosco'}
                  </button>
                  <ul className="space-y-2.5 text-[13px] text-slate-600 dark:text-slate-300">
                    {(plan.features || []).map((f: string) => (
                      <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{f}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <p className="text-center text-slate-400 text-xs mt-8">Preços ilustrativos · confirma planos e limites antes do lançamento.</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-24 px-4 bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 lp-reveal">
            <p className="lp-grad-green uppercase text-[10px] tracking-[0.16em] font-extrabold">FAQ</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] mt-3 mb-3 lp-grad-dark">Dúvidas? Vamos simplificar.</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">O essencial para começares com confiança.</p>
          </div>
          <div className="space-y-3 lp-reveal">
            {faqs.map((f, i) => (
              <div key={i} className="border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left font-bold text-sm text-slate-900 dark:text-white">
                  {f.q}
                  <span className={`text-emerald-500 text-xl font-light transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && <p className="px-5 pb-5 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
        {/* Ad dentro da secção FAQ — herda o fundo e fica com espaçamento
            uniforme acima/abaixo (mt vs padding inferior da secção). */}
        <div className="max-w-screen-xl mx-auto mt-14 sm:mt-16 lp-reveal">
          <AdZone slot="landing_bottom" requireFreePlan={false} />
        </div>
      </section>

      {/* CTA + LEAD FORM */}
      <section id="contact" className="py-20 sm:py-24 px-4 bg-[linear-gradient(120deg,#071426,#0b1b30)] text-slate-50">
        <div className="max-w-screen-xl mx-auto grid lg:grid-cols-2 gap-12 items-center lp-reveal">
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-[-0.04em] leading-tight lp-grad-light">A tua próxima loja começa aqui.</h2>
            <p className="text-slate-300/90 text-sm mt-4 max-w-md mx-auto lg:mx-0 leading-relaxed">Constrói a tua montra online, importa o catálogo e prepara-te para vender de forma mais simples.</p>
            <button onClick={() => navigate('/signup')} className="mt-7 bg-emerald-500 hover:bg-emerald-400 text-[#03291F] px-8 py-4 rounded-full font-bold text-sm transition-colors inline-flex items-center gap-2">
              Quero criar a minha loja <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[11px] text-slate-400 mt-4">Sem cartão de crédito · Configuração rápida</p>
            <a href="mailto:info@lojapilot.pt" className="mt-4 inline-flex items-center gap-2 text-xs text-slate-300 hover:text-emerald-400 transition-colors">
              <Mail className="w-3.5 h-3.5" /> info@lojapilot.pt
            </a>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border dark:border-slate-800 shadow-2xl text-slate-900 dark:text-white">
            <h3 className="text-xl font-bold mb-1">Falar connosco</h3>
            <p className="text-slate-500 text-xs mb-6">Equipa ou plano Business? Preenche e entramos em contacto.</p>
            <form onSubmit={handleLeadSubmit} className="space-y-4">
              <div>
                <Label>Nome Completo</Label>
                <Input required value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} className="mt-1 dark:bg-slate-800 dark:border-slate-700 w-full" />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input required type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className="mt-1 dark:bg-slate-800 dark:border-slate-700 w-full" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Indicativo</Label>
                  <Input value={leadForm.countryCode} onChange={(e) => setLeadForm({ ...leadForm, countryCode: e.target.value })} className="mt-1 dark:bg-slate-800 dark:border-slate-700 w-full" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Telefone / WhatsApp</Label>
                  <Input required type="tel" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className="mt-1 dark:bg-slate-800 dark:border-slate-700 w-full" />
                </div>
              </div>
              <Button type="submit" disabled={isSubmittingLead} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#03291F] mt-2 py-5 text-base rounded-xl font-bold">
                {isSubmittingLead ? "A enviar..." : "Falar com Vendas"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-4 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5">
          <img src={theme === 'dark' ? logoFullLight : logoFullDark} alt="LojaPilot" className="h-12 object-contain" />
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} LojaPilot. Todos os direitos reservados.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
            <button onClick={() => navigate('/privacidade')} className="text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline">
              Privacidade
            </button>
            <button onClick={() => navigate('/cookies')} className="text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline">
              Cookies
            </button>
            <a href="mailto:info@lojapilot.pt" className="text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline">info@lojapilot.pt</a>
            <a
              href="https://www.livroreclamacoes.pt/"
              target="_blank"
              rel="noopener noreferrer"
              title="Livro de Reclamações Eletrónico"
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-600/30 bg-red-600/5 px-2.5 py-1.5 hover:bg-red-600/10 transition-colors"
            >
              <BookOpen className="w-4 h-4 text-red-600" />
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-red-600">Livro de Reclamações</span>
            </a>
          </div>
        </div>
      </footer>

      {/* CHECKOUT MODAL */}
      {showCheckoutModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-3xl p-6 sm:p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => { setShowCheckoutModal(false); setClientSecret(null); }} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Finalizar Subscrição</h3>
            {!clientSecret ? (
              <form onSubmit={handleCheckoutSubmit} className="space-y-4 mt-4">
                <div>
                  <Label>O teu nome</Label>
                  <Input required value={checkoutData.name} onChange={(e) => setCheckoutData({ ...checkoutData, name: e.target.value })} className="mt-1 dark:bg-slate-800 dark:border-slate-700" />
                </div>
                <div>
                  <Label>Email (Acesso à conta)</Label>
                  <Input required type="email" value={checkoutData.email} onChange={(e) => setCheckoutData({ ...checkoutData, email: e.target.value })} className="mt-1 dark:bg-slate-800 dark:border-slate-700" />
                </div>
                <div>
                  <Label>URL da tua Loja WooCommerce</Label>
                  <Input required value={checkoutData.url} onChange={(e) => setCheckoutData({ ...checkoutData, url: e.target.value })} placeholder="ex: minhaloja.pt" className="mt-1 dark:bg-slate-800 dark:border-slate-700" />
                </div>
                <div>
                  <Label>Nº de Lojas</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</Button>
                    <span className="font-bold dark:text-white">{quantity}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => setQuantity(quantity + 1)}>+</Button>
                  </div>
                </div>
                <Button type="submit" disabled={isRedirecting} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#03291F] py-5 text-base rounded-xl font-bold">
                  {isRedirecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> A gerar checkout seguro...</> : "Pagar com Stripe"}
                </Button>
              </form>
            ) : (
              <div className="h-[520px] mt-2 -mx-2">
                {import.meta.env.VITE_STRIPE_PUBLIC_KEY ? (
                  <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                ) : (
                  <div className="flex items-center justify-center h-full text-rose-500 text-sm text-center px-4">
                    Aviso: A chave pública do Stripe (VITE_STRIPE_PUBLIC_KEY) não está configurada no ficheiro .env.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fallback quando o chat agent está bloqueado (ad-blocker/CSP/rede):
          em vez de ficar sem assistente, oferecemos contacto direto. */}
      {chatBlocked && (
        <button
          onClick={() => scrollTo('contact')}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-2xl shadow-emerald-900/30 hover:bg-emerald-500 transition-colors"
          aria-label="Falar connosco"
        >
          <MessageCircle className="w-5 h-5" />
          Falar connosco
        </button>
      )}
    </div>
  );
}
